import { useState, useEffect, useRef } from "react";
import { usePortailAuth } from "../lib/auth-context";
import { usePortailRouter } from "../lib/router-context";
import { authFetch } from "../lib/authFetch";
import {
  ArrowLeft, Loader2, AlertCircle, Send, LifeBuoy, User,
  Hash, Calendar, Video, Truck, MessageSquare, Store, Package,
  ShieldCheck, ClipboardList,
} from "lucide-react";

interface TicketMessage {
  id: string;
  auteur: string;
  contenu: string;
  createdAt: string;
  editedAt: string | null;
  estRevendeur: boolean;
}

interface TicketPiece {
  id: string;
  quantite: number;
  sousGarantie: boolean;
  puduMaterialRequest: string | null;
  trackingNumber: string | null;
  dateReceptionPrevue: string | null;
  destinataire: string | null;
  adresseLivraison: string | null;
  produit: {
    sku: string | null;
    nomFr: string | null;
    imageUrl: string | null;
  } | null;
}

interface TicketDetail {
  id: string;
  ticketNumber: string;
  titre: string | null;
  status: string;
  priority: string;
  contactName: string | null;
  contactPhone: string | null;
  contactEmail: string | null;
  problemDescription: string | null;
  serialNumberFree: string | null;
  datePanne: string | null;
  videoUrl: string | null;
  clientFinalNom: string | null;
  livraisonNomEntreprise: string | null;
  livraisonAdresse: string | null;
  livraisonContact: string | null;
  livraisonPhone: string | null;
  puduWarrantyActive: boolean;
  puduWorkOrder: string | null;
  pieces: TicketPiece[];
  createdAt: string;
  messages: TicketMessage[];
}

const STATUS_LABELS: Record<string, string> = {
  nouveau: "Nouveau",
  en_cours: "En cours",
  en_attente_piece: "En attente pièce",
  resolu: "Résolu",
  ferme: "Fermé",
};

const STATUS_COLORS: Record<string, string> = {
  nouveau: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  en_cours: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
  en_attente_piece: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400",
  resolu: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
  ferme: "bg-secondary text-muted-foreground",
};

function formatDate(d: string | null) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function formatDateShort(d: string | null) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });
}

function formatReceptionDate(d: string | null) {
  if (!d) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(d);
  if (!match) return null;
  const [, year, month, day] = match;
  return `${day}/${month}/${year}`;
}

const DESTINATAIRE_LABELS: Record<string, string> = {
  jobotto: "Jobotto",
  revendeur: "Revendeur",
  client_final: "Client final",
};

export default function TicketDetailPage({ id }: { id: string }) {
  const { portailUser, brand } = usePortailAuth();
  const { navigate } = usePortailRouter();
  const primary = brand?.couleurPrimaire || "#2563eb";

  const [ticket, setTicket] = useState<TicketDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  async function loadTicket() {
    setLoading(true);
    setError(null);
    try {
      const res = await authFetch(`/api/portail/tickets/${id}`);
      if (!res.ok) {
        setError("Ticket non trouvé.");
        return;
      }
      const data = await res.json();
      setTicket(data);
    } catch {
      setError("Erreur de chargement.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadTicket(); }, [id]);

  useEffect(() => {
    if (bottomRef.current) bottomRef.current.scrollIntoView({ behavior: "smooth" });
  }, [ticket?.messages]);

  async function handleSend(e: React.FormEvent) {
    e.preventDefault();
    if (!reply.trim()) return;
    setSending(true);
    setSendError(null);
    try {
      const res = await authFetch(`/api/portail/tickets/${id}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ contenu: reply.trim() }),
      });
      if (!res.ok) {
        const err = await res.json();
        setSendError(err.message || "Erreur envoi.");
        return;
      }
      const msg = await res.json();
      setTicket(prev => prev ? { ...prev, messages: [...prev.messages, msg] } : prev);
      setReply("");
    } catch {
      setSendError("Erreur réseau.");
    } finally {
      setSending(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !ticket) {
    return (
      <div className="p-6 space-y-4">
        <button onClick={() => navigate('/tickets')} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors">
          <ArrowLeft className="w-4 h-4" /> Retour aux tickets
        </button>
        <div className="flex items-center gap-2 text-destructive p-4 bg-destructive/10 rounded-xl text-sm">
          <AlertCircle className="w-4 h-4" /> {error || "Ticket introuvable."}
        </div>
      </div>
    );
  }

  const authorName = `${portailUser?.prenom || ''} ${portailUser?.nom || ''}`.trim() || portailUser?.email || "Vous";

  return (
    <div className="max-w-4xl p-6 space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-1">
          <button onClick={() => navigate('/tickets')} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-2" data-testid="button-back-tickets">
            <ArrowLeft className="w-4 h-4" /> Retour aux tickets
          </button>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-mono text-muted-foreground">{ticket.ticketNumber}</span>
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[ticket.status] || 'bg-secondary text-muted-foreground'}`}>
              {STATUS_LABELS[ticket.status] || ticket.status}
            </span>
          </div>
          <h1 className="text-xl font-bold text-foreground">{ticket.titre || ticket.ticketNumber || "Ticket SAV"}</h1>
          <p className="text-sm text-muted-foreground">Ouvert le {formatDate(ticket.createdAt)}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Colonne gauche: infos */}
        <div className="lg:col-span-1 space-y-4">
          {/* Infos principales */}
          <div className="bg-card border border-border/50 rounded-xl p-4 space-y-3">
            <h3 className="text-sm font-semibold text-foreground">Informations</h3>
            <div className="space-y-2 text-sm">
              {ticket.contactName && (
                <div className="flex items-start gap-2 text-muted-foreground">
                  <User className="w-4 h-4 mt-0.5 shrink-0" />
                  <div>
                    <p className="font-medium text-foreground">{ticket.contactName}</p>
                    {ticket.contactEmail && <p className="text-xs">{ticket.contactEmail}</p>}
                    {ticket.contactPhone && <p className="text-xs">{ticket.contactPhone}</p>}
                  </div>
                </div>
              )}
              {ticket.clientFinalNom && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Store className="w-4 h-4 shrink-0" />
                  <span>Client final : <span className="text-foreground font-medium">{ticket.clientFinalNom}</span></span>
                </div>
              )}
              {ticket.serialNumberFree && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Hash className="w-4 h-4 shrink-0" />
                  <span>S/N : <span className="text-foreground font-mono text-xs">{ticket.serialNumberFree}</span></span>
                </div>
              )}
              {ticket.datePanne && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Calendar className="w-4 h-4 shrink-0" />
                  <span>Panne le : <span className="text-foreground">{formatDateShort(ticket.datePanne)}</span></span>
                </div>
              )}
            </div>
          </div>

          {/* Description */}
          {ticket.problemDescription && (
            <div className="bg-card border border-border/50 rounded-xl p-4 space-y-2">
              <h3 className="text-sm font-semibold text-foreground flex items-center gap-1.5">
                <LifeBuoy className="w-4 h-4" /> Description
              </h3>
              <p className="text-sm text-muted-foreground whitespace-pre-wrap">{ticket.problemDescription}</p>
            </div>
          )}

          {/* Pièces */}
          {ticket.pieces.length > 0 && (
            <div className="bg-card border border-border/50 rounded-xl p-4 space-y-3 overflow-hidden">
              <h3 className="text-sm font-semibold text-foreground flex items-center gap-1.5">
                <Package className="w-4 h-4" /> Pièces ({ticket.pieces.length})
              </h3>
              {(ticket.puduWarrantyActive || ticket.puduWorkOrder) && (
                <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-1.5">
                  {ticket.puduWarrantyActive && (
                    <span className="inline-flex flex-wrap items-center gap-1 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 px-2 py-0.5 text-[11px] font-medium">
                      <ShieldCheck className="w-3 h-3 shrink-0" /> Garantie Pudu
                    </span>
                  )}
                  {ticket.puduWorkOrder && (
                    <span className="inline-flex flex-wrap items-center gap-x-1 gap-y-0.5 rounded-full bg-secondary text-foreground px-2 py-0.5 text-[11px] font-medium min-w-0">
                      <ClipboardList className="w-3 h-3 shrink-0" />
                      <span>Work Order Pudu :</span>
                      <span className="min-w-0 break-all">{ticket.puduWorkOrder}</span>
                    </span>
                  )}
                </div>
              )}
              <div className="space-y-3">
                {ticket.pieces.map((piece) => {
                  const receptionDate = formatReceptionDate(piece.dateReceptionPrevue);
                  const destinataireLabel = piece.destinataire
                    ? DESTINATAIRE_LABELS[piece.destinataire.toLowerCase()]
                    : null;
                  return (
                    <div key={piece.id} className="flex gap-3 min-w-0 border-t border-border/50 pt-3 first:border-t-0 first:pt-0">
                      <div className="relative w-12 h-12 shrink-0 rounded-lg bg-secondary overflow-hidden flex items-center justify-center">
                        {piece.produit?.imageUrl ? (
                          <>
                            <img
                              src={piece.produit.imageUrl}
                              alt=""
                              className="w-full h-full object-cover"
                              onError={(event) => {
                                event.currentTarget.classList.add("hidden");
                                event.currentTarget.nextElementSibling?.classList.remove("hidden");
                              }}
                            />
                            <Package className="hidden w-5 h-5 text-muted-foreground" />
                          </>
                        ) : (
                          <Package className="w-5 h-5 text-muted-foreground" />
                        )}
                      </div>
                      <div className="min-w-0 flex-1 space-y-1.5 text-sm">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            {piece.produit?.nomFr && <p className="font-medium text-foreground break-words">{piece.produit.nomFr}</p>}
                            {piece.produit?.sku && <p className="text-xs text-muted-foreground">SKU : {piece.produit.sku}</p>}
                          </div>
                          {piece.quantite > 1 && <span className="text-xs text-muted-foreground shrink-0">× {piece.quantite}</span>}
                        </div>
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
                          {piece.sousGarantie && (
                            <span className="rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 px-2 py-0.5 font-medium">Sous garantie</span>
                          )}
                          {piece.puduMaterialRequest && <span>N° SP : {piece.puduMaterialRequest}</span>}
                          {piece.trackingNumber && <span className="break-all">Suivi : {piece.trackingNumber}</span>}
                          {receptionDate && <span>Réception prévue : {receptionDate}</span>}
                          {destinataireLabel && <span>Destinataire : {destinataireLabel}</span>}
                        </div>
                        {piece.adresseLivraison && <p className="text-xs text-muted-foreground whitespace-pre-line break-words">Adresse : {piece.adresseLivraison}</p>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Vidéo */}
          {ticket.videoUrl && (
            <div className="bg-card border border-border/50 rounded-xl p-4 space-y-2">
              <h3 className="text-sm font-semibold text-foreground flex items-center gap-1.5">
                <Video className="w-4 h-4" /> Vidéo jointe
              </h3>
              <video src={ticket.videoUrl} controls className="w-full rounded-lg max-h-48 object-cover" data-testid="video-ticket" />
            </div>
          )}

          {/* Livraison */}
          {(ticket.livraisonNomEntreprise || ticket.livraisonAdresse) && (
            <div className="bg-card border border-border/50 rounded-xl p-4 space-y-2">
              <h3 className="text-sm font-semibold text-foreground flex items-center gap-1.5">
                <Truck className="w-4 h-4" /> Livraison
              </h3>
              <div className="text-sm text-muted-foreground space-y-1">
                {ticket.livraisonNomEntreprise && <p className="text-foreground font-medium">{ticket.livraisonNomEntreprise}</p>}
                {ticket.livraisonAdresse && <p>{ticket.livraisonAdresse}</p>}
                {ticket.livraisonContact && <p>{ticket.livraisonContact}</p>}
                {ticket.livraisonPhone && <p>{ticket.livraisonPhone}</p>}
              </div>
            </div>
          )}
        </div>

        {/* Colonne droite: messages */}
        <div className="lg:col-span-2 flex flex-col gap-4">
          <div className="bg-card border border-border/50 rounded-xl flex flex-col" style={{ minHeight: 400 }}>
            <div className="p-4 border-b border-border/60">
              <h3 className="text-sm font-semibold text-foreground flex items-center gap-1.5">
                <MessageSquare className="w-4 h-4" /> Discussion
                <span className="text-xs font-normal text-muted-foreground ml-1">({ticket.messages.length} message{ticket.messages.length > 1 ? 's' : ''})</span>
              </h3>
            </div>
            <div className="flex-1 p-4 space-y-3 overflow-y-auto" style={{ maxHeight: 400 }}>
              {ticket.messages.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-8">Aucun message pour le moment.</p>
              )}
              {ticket.messages.map(msg => {
                const isMe = msg.estRevendeur;
                return (
                  <div key={msg.id} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`} data-testid={`message-${msg.id}`}>
                    <div className={`max-w-[85%] rounded-xl px-4 py-2.5 text-sm space-y-1 ${isMe ? 'text-white' : 'bg-secondary text-foreground'}`}
                      style={isMe ? { backgroundColor: primary } : {}}>
                      <p className={`text-xs font-medium ${isMe ? 'text-white/70' : 'text-muted-foreground'}`}>{msg.auteur}</p>
                      <p className="whitespace-pre-wrap">{msg.contenu}</p>
                      <p className={`text-xs ${isMe ? 'text-white/50' : 'text-muted-foreground'}`}>{formatDate(msg.createdAt)}</p>
                    </div>
                  </div>
                );
              })}
              <div ref={bottomRef} />
            </div>

            {ticket.status !== 'ferme' && (
              <form onSubmit={handleSend} className="p-4 border-t border-border/60">
                {sendError && <p className="text-xs text-destructive mb-2">{sendError}</p>}
                <div className="flex items-end gap-2">
                  <textarea
                    className="flex-1 px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none"
                    rows={2}
                    placeholder="Votre réponse..."
                    value={reply}
                    onChange={e => setReply(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(e as any); } }}
                    data-testid="input-reply"
                  />
                  <button
                    type="submit"
                    disabled={sending || !reply.trim()}
                    className="p-2.5 rounded-lg text-white disabled:opacity-50 transition-colors hover:opacity-90"
                    style={{ backgroundColor: primary }}
                    data-testid="button-send-reply"
                  >
                    {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </button>
                </div>
              </form>
            )}
            {ticket.status === 'ferme' && (
              <div className="p-4 border-t border-border/60 text-center text-sm text-muted-foreground">
                Ce ticket est fermé. Créez un nouveau ticket si nécessaire.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
